import {reactive} from 'vue';
import ProductListItem from '../../types/ProductListItem';
import FilterDataItem from '../../types/FilterDataItem';
import PagingData from '../../types/PagingData';
import Category from '../../types/Category';
import FilterOption from '../../types/FilterOption';
import type SortOption from '../../types/SortOption';
import {Parameters, ListElementPlacementMode} from '../../services/productsList/ProductsListService';

class ProductListStore {
  protected _elementsList: { elements: ProductListItem[], insertions: ProductListItem[] } = {elements: [], insertions: []};
  protected _filtersData: FilterDataItem[] = [];
  protected _pagingData: PagingData;
  protected _sortingData: SortOption[];
  protected _currentCategory: Category;
  protected _categoryForContent: Category;
  protected _activeVirtualFilterCategory: boolean;
  protected _activePhysicalFilterCategory: boolean;
  protected _showPersonalizationBanner: boolean;

  protected _loadedPages: number[] = []; // used for endless scrolling

  public filterOriginalParams: Parameters = {};
  public filterCurrentParams: Parameters = {};
  public filterPageLoad = {url: '', reload: false};

  constructor() {
    if (window.__productListConfig) {
      this.setData(window.__productListConfig.productListStore);
    }
  }

  setData(productListStore: ProductListStore, elementReplacementMode: ListElementPlacementMode = 'replace') {
    if (elementReplacementMode === 'prepend') {
      this.elementsList = {
        elements: productListStore.elementsList.elements.concat(this.elementsList.elements),
        insertions: productListStore.elementsList.insertions.concat(this.elementsList.insertions)
      };
    } else if (elementReplacementMode === 'append') {
      this.elementsList = {
        elements: this.elementsList.elements.concat(productListStore.elementsList.elements),
        insertions: this.elementsList.insertions.concat(productListStore.elementsList.insertions)
      };
    } else {
      this.elementsList = productListStore.elementsList;
    }

    this.filtersData = productListStore.filtersData;
    this.pagingData = productListStore.pagingData;
    this.sortingData = productListStore.sortingData;
    this.currentCategory = productListStore.currentCategory;
    this.categoryForContent = productListStore.categoryForContent;
    this.activeVirtualFilterCategory = productListStore.activeVirtualFilterCategory;
    this.activePhysicalFilterCategory = productListStore.activePhysicalFilterCategory;
    this.showPersonalizationBanner = productListStore.showPersonalizationBanner;

    if (productListStore.loadedPages) {
      this.loadedPages = productListStore.loadedPages;
    }
  }

  get elementsList(): { elements: ProductListItem[], insertions: ProductListItem[] } {
    return this._elementsList;
  }

  set elementsList(data: { elements: ProductListItem[], insertions: ProductListItem[] }) {
    this._elementsList.elements.splice(0, this._elementsList.elements.length, ...data.elements);
    this._elementsList.insertions.splice(0, this._elementsList.insertions.length, ...data.insertions);
  }

  get filtersData(): FilterDataItem[] {
    return this._filtersData;
  }

  set filtersData(data: FilterDataItem[]) {
    this._filtersData = data;
  }

  get pagingData(): PagingData {
    return this._pagingData;
  }

  get currentCategory(): Category {
    return this._currentCategory;
  }

  set currentCategory(data: Category) {
    this._currentCategory = data;
  }

  get categoryForContent(): Category {
    return this._categoryForContent;
  }

  set categoryForContent(data: Category) {
    this._categoryForContent = data;
  }

  set pagingData(data: PagingData) {
    this._pagingData = data;
  }

  get sortingData(): SortOption[] {
    return this._sortingData;
  }

  set sortingData(data: SortOption[]) {
    this._sortingData = data;
  }

  get activeVirtualFilterCategory(): boolean {
    return this._activeVirtualFilterCategory;
  }

  set activeVirtualFilterCategory(data: boolean) {
    this._activeVirtualFilterCategory = data;
  }

  get activePhysicalFilterCategory(): boolean {
    return this._activePhysicalFilterCategory;
  }

  set activePhysicalFilterCategory(data: boolean) {
    this._activePhysicalFilterCategory = data;
  }

  get showPersonalizationBanner(): boolean {
    return this._showPersonalizationBanner;
  }

  set showPersonalizationBanner(data: boolean) {
    this._showPersonalizationBanner = data;
  }

  get loadedPages(): number[] {
    return this._loadedPages;
  }

  set loadedPages(data: number[]) {
    this._loadedPages = data;
  }

  addLoadedPage(page: number) {
    this._loadedPages.push(page);
    this._loadedPages.sort((a, b) => a - b);
  }

  getSearchQuery(): string {
    return window.__productListConfig.searchQuery;
  }

  getFilterByName(name: string): FilterDataItem|null {
    let ndx: number = this._filtersData.length;
    while (ndx--) {
      if (name === this._filtersData[ndx].name) {
        return this._filtersData[ndx]
      }
    }

    return null;
  }

  getActiveFilters(): {[key: string]: FilterOption[]} {
    const activeFilters: {[key: string]: FilterOption[]} = {};
    this._filtersData.forEach((filterData) => {
      const activeOptions: FilterOption[] = filterData.options
        .filter(f => f.selected === true);

      if (activeOptions.length && filterData.name !== 'qc') {
        activeFilters[filterData.name] = activeOptions;
      }
    });

    return activeFilters;
  }

  getActiveSort(): SortOption|null {
    const selected: SortOption[] = this._sortingData.filter(sortOption => sortOption.selected === true);
    if (selected.length) {
      return selected[0];
    }

    return null;
  }

  updateProduct(data) {
    for (let i = 0, l = this._elementsList.elements.length; i < l; i += 1) {
      if (this._elementsList.elements[i].type === 'product' && data.id === this._elementsList.elements[i].data.id && data.itemId === this._elementsList.elements[i].data.itemId) {
        this._elementsList.elements[i].data = data;
        break;
      }
    }
  }

  addToBasket(data) {
    for (let i = 0, l = this._elementsList.elements.length; i < l; i += 1) {
      if (this._elementsList.elements[i].type === 'product' && data.id === this._elementsList.elements[i].data.id && data.itemId === this._elementsList.elements[i].data.itemId) {
        this._elementsList.elements[i].data.inBasket = data.inBasket;
        break;
      }
    }
  }

  addSuccessorToBasket(itemId) {
    for (let i = 0, l = this._elementsList.elements.length; i < l; i += 1) {
      if (this._elementsList.elements[i].type === 'product' && itemId === this._elementsList.elements[i].data.itemId) {
        this._elementsList.elements[i].data.successorInBasket = true;
        break;
      }
    }
  }

  addToWishlist(productId, itemId) {
    for (let i = 0, l = this._elementsList.elements.length; i < l; i += 1) {
      if (this._elementsList.elements[i].type === 'product' && productId === this._elementsList.elements[i].data.id && itemId === this._elementsList.elements[i].data.itemId) {
        this._elementsList.elements[i].data.inWishlist = true;
        break;
      }
    }
  }

  removeFromWishlist(productId, itemId) {
    for (let i = 0, l = this._elementsList.elements.length; i < l; i += 1) {
      if (this._elementsList.elements[i].type === 'product' && productId === this._elementsList.elements[i].data.id && itemId === this._elementsList.elements[i].data.itemId) {
        this._elementsList.elements[i].data.inWishlist = false;
        break;
      }
    }
  }

  protected getCacheKey() {
    return 'productsListStore:data' + location.pathname;
  }

  saveToCache() {
    const storeProperties = Object.keys(Object.getOwnPropertyDescriptors(this))
      .filter(p => p.match(/^_[^_]+/))
      .map(p => p.replace('_', ''));

    const data = {}
    storeProperties.forEach((property) => {
      data[property] = this[property];
    });

    try {
      sessionStorage.setItem(this.getCacheKey(), JSON.stringify(data));
    } catch (error) {
      // fail silently, but log
      window.onerror('Error while trying to save ProductListStore to SessionStorage');
    }
  }

  restoreFromCache(): boolean {
    const data = sessionStorage.getItem(this.getCacheKey());
    if (data) {
      this.setData(JSON.parse(data));
      return true;
    }

    return false;
  }

  clearCache() {
    sessionStorage.removeItem(this.getCacheKey());
  }
}

const productListStore: ProductListStore = new ProductListStore();
export default reactive(productListStore);
export {ProductListStore};
